// 引入必要的模組與檔案
import React, { useState, useEffect, useRef } from 'react';
import axios from './utils/axiosConfig';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { useAuth } from './context/AuthContext'; // 引入 AuthContext 來取得用戶資訊
import { hasPermission } from './utils/permissions'; // 引入權限檢查工具函數
import { isSuperAdmin } from './utils/superAdmin';

// Component 定義
const SqltableorderTable = () => {

  // 狀態宣告區域 - 用於管理前端資料及顯示控制
  const [tableData, setTableData] = useState([]); // 儲存表格資料
  const [loading, setLoading] = useState(false); // 控制加載狀態
  const [filter, setFilter] = useState(''); // 搜尋過濾條件
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' }); // 排序設定
  const [showModal, setShowModal] = useState(false); // 控制 Modal 顯示
  const [selectedItem, setSelectedItem] = useState(null); // 當前選擇的項目
  const [successMessage, setSuccessMessage] = useState(''); // 成功訊息顯示
  const token = localStorage.getItem('token'); // 取得 token 以供授權
  const [formData, setFormData] = useState({}); // 表單資料
  const { user } = useAuth();
  const [canEdit, setCanEdit] = useState(false);

  // 🔒 嚴格權限控制：只有 e_id=80 (超級管理員) 才能存取設定專區
  useEffect(() => {
    const checkPermission = async () => {
      // 只允許 e_id=80 存取設定專區
      const superAdminFlag = isSuperAdmin(user?.e_id);
      setCanEdit(superAdminFlag);
      
      if (!superAdminFlag && user) {
        console.log(`設定專區存取拒絕：用戶 ${user.e_id} 試圖存取設定專區，但只有 e_id=80 才有權限`);
      }
    };
    checkPermission();
  }, [user]);

  // 副作用 (useEffect) - 資料讀取
  useEffect(() => {
    fetchTableData(); // 加載表格資料
  }, [filter, sortConfig]);

  // 獲取表格資料
  const fetchTableData = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/sqltableorder', {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          filter,
          sortKey: sortConfig.key,
          sortDirection: sortConfig.direction
        }
      });
      setTableData(response.data);
    } catch (error) {
      // 獲取資料失敗
    } finally {
      setLoading(false);
    }
  };

  // 處理排序
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 處理編輯
  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      table_name: item.table_name || '',
      table_title_eng: item.table_title_eng || '',
      table_title_cht: item.table_title_cht || '',
      table_width: item.table_width || '',
      table_order: item.table_order || '',
      table_view: item.table_view || 0,
      table_whole_edit: item.table_whole_edit || 0,
      table_outexl: item.table_outexl || 0,
      table_view_1: item.table_view_1 || 0,
      table_whole_edit_1: item.table_whole_edit_1 || 0
    });
    setShowModal(true);
  };

  // 處理表單提交
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/sqltableorder/${selectedItem.id}`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSuccessMessage('更新成功！');
      setShowModal(false);
      fetchTableData(); // 重新載入資料
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert('更新失敗，請重試');
    }
  };

  // 處理表單輸入變更
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));
  };

  // 🔒 權限檢查：只有 e_id=80 才能存取設定專區
  if (user && !canEdit) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger">
          <h4>🚫 存取被拒絕</h4>
          <p>您沒有權限存取設定專區。只有超級管理員 (e_id=80) 才能使用此功能。</p>
          <p>您的用戶 ID: {user.e_id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <h2>SQL 表格順序設定</h2>
      
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      {/* 搜尋欄位 */}
      <div className="mb-3">
        <Form.Control
          type="text"
          placeholder="搜尋表格名稱或中文標題..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {/* 表格顯示 */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>
              ID {sortConfig.key === 'id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('table_name')} style={{ cursor: 'pointer' }}>
              表格名稱 {sortConfig.key === 'table_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('table_title_cht')} style={{ cursor: 'pointer' }}>
              中文標題 {sortConfig.key === 'table_title_cht' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('table_title_eng')} style={{ cursor: 'pointer' }}>
              英文標題 {sortConfig.key === 'table_title_eng' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th onClick={() => handleSort('table_order')} style={{ cursor: 'pointer' }}>
              順序 {sortConfig.key === 'table_order' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
            </th>
            <th>寬度</th>
            <th>檢視</th>
            <th>編輯</th>
            {canEdit && <th>操作</th>}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="9" className="text-center">載入中...</td>
            </tr>
          ) : tableData.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center">沒有資料</td>
            </tr>
          ) : (
            tableData.map((item) => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.table_name}</td>
                <td>{item.table_title_cht}</td>
                <td>{item.table_title_eng}</td>
                <td>{item.table_order}</td>
                <td>{item.table_width}</td>
                <td>{item.table_view ? '是' : '否'}</td>
                <td>{item.table_whole_edit ? '是' : '否'}</td>
                {canEdit && (
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleEdit(item)}
                    >
                      編輯
                    </Button>
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* 編輯 Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>編輯表格設定</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>表格名稱</Form.Label>
                  <Form.Control
                    type="text"
                    name="table_name"
                    value={formData.table_name || ''}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>英文標題</Form.Label>
                  <Form.Control
                    type="text"
                    name="table_title_eng"
                    value={formData.table_title_eng || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>中文標題</Form.Label>
                  <Form.Control
                    type="text"
                    name="table_title_cht"
                    value={formData.table_title_cht || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>表格寬度</Form.Label>
                  <Form.Control
                    type="text"
                    name="table_width"
                    value={formData.table_width || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>順序</Form.Label>
                  <Form.Control
                    type="number"
                    name="table_order"
                    value={formData.table_order || ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="table_view"
                    label="可檢視"
                    checked={formData.table_view === 1}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="table_whole_edit"
                    label="可整體編輯"
                    checked={formData.table_whole_edit === 1}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="table_outexl"
                    label="可匯出Excel"
                    checked={formData.table_outexl === 1}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="table_view_1"
                    label="檢視權限1"
                    checked={formData.table_view_1 === 1}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-3">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="table_whole_edit_1"
                    label="編輯權限1"
                    checked={formData.table_whole_edit_1 === 1}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                取消
              </Button>
              <Button variant="primary" type="submit">
                儲存變更
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SqltableorderTable;
